import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useBrand } from '../../app/config/brand';
import { useIdentityRefused } from '../contexts/IdentityRefusedContext';

/**
 * Human name of the sign-in method behind a connection provider string.
 * Mirrors `connectionDisplayName` in the api-gateway deny-policy module
 * (apps/api-gateway/src/auth/identity-unification/deny-policy.ts) — apps
 * can't share constants.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function connectionDisplayName(provider: string): string {
  switch (provider) {
    case 'google-oauth2':
      return 'Google';
    case 'auth0':
      return 'email and password';
    default:
      return provider || 'your original sign-in method';
  }
}

/**
 * Deny-only identity refusal banner (WOL-393/WOL-471).
 *
 * The api-gateway refuses to provision an account for a sign-in whose email
 * already has an account under ANOTHER sign-in method (one account per
 * email; identities are never linked). It signals the refusal via the
 * `X-Identity-Refused` response header — captured centrally in `useApi`'s
 * fetchWithAuth, mirroring the X-Reauth-Required pattern — carrying the
 * original connection. This banner tells the user which method to sign back
 * in with. Non-blocking (like ReauthPrompt): the session itself is valid,
 * it just has no account and never will.
 */
const IdentityRefusedBanner: React.FC = () => {
  const { refusedConnection } = useIdentityRefused();
  const { logout } = useAuth0();
  const brand = useBrand();

  if (refusedConnection === null) return null;

  const methodName = connectionDisplayName(refusedConnection);

  const handleSwitch = () => {
    // Sign the wrong-method session out; the user signs back in with the
    // original method from the landing page.
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[90] w-full max-w-md px-4"
      data-testid="identity-refused-banner"
    >
      <div
        className="rounded-xl shadow-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-3"
        style={{ backgroundColor: brand.colors.bgCard, border: `1px solid ${brand.colors.border}` }}
      >
        <div className="flex-1">
          <p className="text-sm font-semibold mb-1" style={{ color: brand.colors.secondary }}>
            You already have an account
          </p>
          <p className="text-sm" style={{ color: brand.colors.textLight }}>
            This email is already registered — sign in with {methodName} to use your
            existing account. A second account can&apos;t be created for the same email.
          </p>
        </div>

        <button
          onClick={handleSwitch}
          className="shrink-0 px-5 py-2.5 text-sm font-medium rounded-full transition-all duration-200"
          style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
        >
          Switch Sign-In
        </button>
      </div>
    </div>
  );
};

export default IdentityRefusedBanner;
